export interface WebinarScheduleEntry {
  title: string;
  date: string; // "25 July 2026"
  day: string; // "Saturday"
  time: string; // "4:00 PM"
  endTime: string; // "6:00 PM"
}

export interface WebinarScheduleData {
  weekly_webinar: WebinarScheduleEntry;
  monthly_webinar: {
    cpa: WebinarScheduleEntry;
    cma: WebinarScheduleEntry;
  };
}

// Per webinar object:
// true  → always show the hardcoded entry in `webinarSchedule` below (use when
//         the Strapi team hasn't updated that webinar's date).
// false → show the date from Strapi, falling back to `webinarSchedule` below
//         if Strapi doesn't return enough events or the fetch fails.
export const SHOW_STATIC_WEBINAR_DATE = {
  weekly_webinar: false, // CAIRA "Build your 1st AI Agent"
  monthly_webinar: false, // Kaun Banega CPA + Kaun Banega CMA
};

export const webinarSchedule: WebinarScheduleData = {
  weekly_webinar: {
    title: "Build your 1st AI Agent in 2 hours",
    date: "24 October 2026",
    day: "Saturday",
    time: "4:00 PM",
    endTime: "6:00 PM",
  },
  monthly_webinar: {
    cpa: {
      title: "Kaun Banega CPA",
      date: "25 October 2026",
      day: "Sunday",
      time: "5:00 PM",
      endTime: "8:00 PM",
    },
    cma: {
      title: "Kaun Banega CMA",
      date: "25 October 2026",
      day: "Sunday",
      time: "2:00 PM",
      endTime: "5:00 PM",
    },
  },
};

export type WebinarKey = "weekly" | "monthly_cpa" | "monthly_cma";

export function getWebinarEntry(
  key: WebinarKey,
  schedule: WebinarScheduleData = webinarSchedule,
): WebinarScheduleEntry {
  switch (key) {
    case "monthly_cpa":
      return schedule.monthly_webinar.cpa;
    case "monthly_cma":
      return schedule.monthly_webinar.cma;
    case "weekly":
    default:
      return schedule.weekly_webinar;
  }
}

// Minimal shape of an `onlineEvents` entry (from `fetchAndFormatEventData`)
// needed to build a `WebinarScheduleEntry`. Kept loose/`any`-tolerant since
// the API itself returns untyped event objects.
interface OnlineEventLike {
  title?: string;
  day?: string;
  category?: string;
  date?: string; // CMS `event_date_string`, e.g. "Oct 17, 2026"
  time?: string; // CMS `event_time_string`, e.g. "4:00 PM (IST)"
  isoDate?: string | null;
  cpe?: { sessionDuration?: string };
}

const DEFAULT_SESSION_MINUTES = 180;

function parseSessionDurationMinutes(sessionDuration?: string): number {
  const match = sessionDuration?.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : DEFAULT_SESSION_MINUTES;
}

function formatIstDate(date: Date): string {
  const day = date.toLocaleDateString("en-US", {
    timeZone: "Asia/Kolkata",
    day: "numeric",
  });
  const month = date.toLocaleDateString("en-US", {
    timeZone: "Asia/Kolkata",
    month: "long",
  });
  const year = date.toLocaleDateString("en-US", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
  });
  return `${day} ${month} ${year}`;
}

function formatIstTime(date: Date): string {
  return date.toLocaleTimeString("en-US", {
    timeZone: "Asia/Kolkata",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

// Parses the CMS's free-text display date/time ("Oct 17, 2026" + "4:00 PM
// (IST)") as an IST datetime. Returns null if either string is unparseable.
export function parseDisplayDateTime(
  date?: string | null,
  time?: string | null,
): Date | null {
  if (!date || !time) return null;
  const day = new Date(date);
  if (isNaN(day.getTime())) return null;
  let parts: { hours: number; minutes: number };
  try {
    parts = parseTimeParts(time);
  } catch {
    return null;
  }
  const pad = (n: number) => String(n).padStart(2, "0");
  const start = new Date(
    `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}T${pad(
      parts.hours,
    )}:${pad(parts.minutes)}:00${IST_OFFSET}`,
  );
  return isNaN(start.getTime()) ? null : start;
}

// Builds a `WebinarScheduleEntry` from an `onlineEvents` item. Prefers the
// CMS display strings (`event_date_string` / `event_time_string`) since
// that's what the editors keep up to date; `sf_webinar_detail.webinar_date`
// (`isoDate`) can lag behind and is only used as a fallback. `endTime` is
// derived from the event's CPE session duration (falls back to 3 hours).
function mapOnlineEventToEntry(
  event: OnlineEventLike | undefined,
): WebinarScheduleEntry | null {
  if (!event) return null;
  const start =
    parseDisplayDateTime(event.date, event.time) ??
    (event.isoDate ? new Date(event.isoDate) : null);
  if (!start || isNaN(start.getTime())) return null;

  const end = new Date(
    start.getTime() +
      parseSessionDurationMinutes(event.cpe?.sessionDuration) * 60000,
  );

  return {
    title: event.title || "",
    date: formatIstDate(start),
    day: event.day || "",
    time: formatIstTime(start),
    endTime: formatIstTime(end),
  };
}

// Maps the `onlineEvents` array returned by `fetchAndFormatEventData` onto
// the weekly/monthly webinar schedule. Matches by `category` (CAIRA ->
// weekly, CMA/CPA -> monthly). Each entry resolves independently: past
// events are already filtered out upstream, so one webinar missing (e.g. the
// monthly one has ended) must not discard the others — only the missing one
// falls back to the static `webinarSchedule`. Returns null only when none of
// the three could be mapped.
export function buildWebinarScheduleFromEvents(
  onlineEvents: OnlineEventLike[] | undefined | null,
): WebinarScheduleData | null {
  if (!onlineEvents || onlineEvents.length === 0) return null;

  const byCategory = (category: string) =>
    mapOnlineEventToEntry(onlineEvents.find((e) => e.category === category));

  const weekly = byCategory("CAIRA");
  const cma = byCategory("CMA");
  const cpa = byCategory("CPA");

  if (!weekly && !cma && !cpa) return null;

  return {
    weekly_webinar:
      SHOW_STATIC_WEBINAR_DATE.weekly_webinar || !weekly
        ? webinarSchedule.weekly_webinar
        : weekly,
    monthly_webinar: {
      cpa:
        SHOW_STATIC_WEBINAR_DATE.monthly_webinar || !cpa
          ? webinarSchedule.monthly_webinar.cpa
          : cpa,
      cma:
        SHOW_STATIC_WEBINAR_DATE.monthly_webinar || !cma
          ? webinarSchedule.monthly_webinar.cma
          : cma,
    },
  };
}

export function isWebinarKey(
  value: string | null | undefined,
): value is WebinarKey {
  return (
    value === "weekly" || value === "monthly_cpa" || value === "monthly_cma"
  );
}

export interface OrdinalDateParts {
  day: number;
  suffix: string;
  month: string;
  year: number;
}

function getOrdinalSuffix(day: number): string {
  if (day >= 11 && day <= 13) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

// Splits "25 July 2026" into parts so JSX can render `{day}<sup>{suffix}</sup> {month}`.
export function getOrdinalDateParts(date: string): OrdinalDateParts {
  const [dayStr, month, yearStr] = date.trim().split(/\s+/);
  const day = parseInt(dayStr, 10);
  return {
    day,
    suffix: getOrdinalSuffix(day),
    month,
    year: parseInt(yearStr, 10),
  };
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const IST_OFFSET = "+05:30";

function parseTimeParts(time: string): { hours: number; minutes: number } {
  const match = time.trim().match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!match) {
    throw new Error(`Invalid webinar time string: ${time}`);
  }
  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const period = match[3].toUpperCase();
  if (period === "PM" && hours !== 12) hours += 12;
  if (period === "AM" && hours === 12) hours = 0;
  return { hours, minutes };
}

// Combines an entry's date + time (IST, this business's local timezone) into a real Date, for countdowns.
export function getWebinarDateTime(
  entry: WebinarScheduleEntry,
  which: "start" | "end" = "start",
): Date {
  const { day, month, year } = getOrdinalDateParts(entry.date);
  const monthIndex = MONTH_NAMES.findIndex(
    (m) => m.toLowerCase() === month.toLowerCase(),
  );
  const { hours, minutes } = parseTimeParts(
    which === "end" ? entry.endTime : entry.time,
  );
  const iso = `${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(
    day,
  ).padStart(2, "0")}T${String(hours).padStart(2, "0")}:${String(
    minutes,
  ).padStart(2, "0")}:00${IST_OFFSET}`;
  return new Date(iso);
}

// False once the session has ended (or if its date can't be parsed), so the
// UI can show generic copy instead of announcing a date that has passed.
export function isWebinarEntryUpcoming(
  entry: WebinarScheduleEntry,
  now: Date = new Date(),
): boolean {
  try {
    const end = getWebinarDateTime(entry, "end");
    return !isNaN(end.getTime()) && end.getTime() >= now.getTime();
  } catch {
    return false;
  }
}

function toICSDateString(date: Date): string {
  return date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
}

// Builds a Google Calendar "add event" link for an "Add to calendar" button.
export function generateGoogleCalendarUrl(
  entry: WebinarScheduleEntry,
  options?: { description?: string; location?: string },
): string {
  const start = getWebinarDateTime(entry, "start");
  const end = getWebinarDateTime(entry, "end");

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: entry.title,
    dates: `${toICSDateString(start)}/${toICSDateString(end)}`,
  });
  if (options?.description) params.set("details", options.description);
  if (options?.location) params.set("location", options.location);

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}
